import type { AppDispatch } from '../../store';
import { cargoApi } from '../../store/api/cargoApi';
import type { CargoLifecycleProjection } from './cargoLifecycleProjectionTypes';

const DEFAULT_CONCURRENCY = 8;

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return [];
  const results: R[] = new Array(items.length);
  let next = 0;

  async function worker() {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i]);
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

/**
 * Single client for GET /cargo/:id/lifecycle-projection.
 * No local activeQuantity recomputation — API is SSOT.
 */
export class CargoLifecycleProjectionClient {
  static async fetchOne(
    dispatch: AppDispatch,
    cargoId: number,
    options?: { forceRefetch?: boolean }
  ): Promise<CargoLifecycleProjection | null> {
    const id = Number(cargoId);
    if (!Number.isFinite(id) || id <= 0) return null;
    try {
      const projection = await dispatch(
        cargoApi.endpoints.getCargoLifecycleProjection.initiate(id, {
          forceRefetch: options?.forceRefetch ?? false,
        })
      ).unwrap();
      return projection ?? null;
    } catch {
      return null;
    }
  }

  static async fetchMany(
    dispatch: AppDispatch,
    cargoIds: number[],
    options?: { forceRefetch?: boolean; concurrency?: number }
  ): Promise<Map<number, CargoLifecycleProjection>> {
    const ids = Array.from(
      new Set(
        cargoIds.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)
      )
    );
    const out = new Map<number, CargoLifecycleProjection>();
    if (ids.length === 0) return out;

    const rows = await mapWithConcurrency(
      ids,
      options?.concurrency ?? DEFAULT_CONCURRENCY,
      async (cargoId) => {
        const projection = await CargoLifecycleProjectionClient.fetchOne(dispatch, cargoId, options);
        return { cargoId, projection };
      }
    );

    for (const { cargoId, projection } of rows) {
      if (projection) out.set(cargoId, projection);
    }
    return out;
  }
}
